import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from './firebaseConfig';

const PENDING_KEY = 'chi_na_jian_pending_visit';
const HISTORY_KEY = 'chi_na_jian_visits';
const ASK_AFTER = 20 * 60000;
const FORGET_AFTER = 3 * 24 * 3600000;
const REVIEW_BUCKETS = [0, 10, 30, 100, 300, 1000, 3000];

const read = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
};
const write = (key, value) => {
  try { value == null ? localStorage.removeItem(key) : localStorage.setItem(key, JSON.stringify(value)); } catch {}
};

export const reviewBucket = count => [...REVIEW_BUCKETS].reverse().find(bucket => (Number.isFinite(count) ? count : 0) >= bucket);

class VisitFeedbackService {
  recordNavigation(place, rank) {
    if (!place?.place_id) return;
    write(PENDING_KEY, {
      placeId: place.place_id,
      name: String(place.name || '').slice(0, 120),
      rating: Number.isFinite(place.rating) ? place.rating : null,
      reviewCount: Number.isFinite(place.user_ratings_total) ? place.user_ratings_total : 0,
      walkMinutes: Number.isFinite(place.walkMinutes) ? place.walkMinutes : null,
      rank: Number.isInteger(rank) && rank >= 1 && rank <= 20 ? rank : null,
      at: Date.now()
    });
  }

  // Ask once the meal is plausibly over; forget visits nobody answered within three days.
  getPrompt(now = Date.now()) {
    const pending = read(PENDING_KEY, null);
    if (!pending || typeof pending.placeId !== 'string' || !Number.isFinite(pending.at)) return null;
    if (now - pending.at > FORGET_AFTER) { write(PENDING_KEY, null); return null; }
    return now - pending.at >= ASK_AFTER ? pending : null;
  }

  async resolve(outcome) {
    const pending = read(PENDING_KEY, null);
    write(PENDING_KEY, null);
    if (!pending?.placeId || !['liked', 'meh', 'skipped'].includes(outcome)) return this.getOutcomes();
    const history = (Array.isArray(read(HISTORY_KEY, [])) ? read(HISTORY_KEY, []) : []).filter(item => item?.placeId !== pending.placeId);
    write(HISTORY_KEY, [{ placeId: pending.placeId, name: pending.name, outcome, at: Date.now() }, ...history].slice(0, 100));
    if (outcome !== 'skipped') {
      // Anonymous and coarse: no place ID, name, user ID or location leaves the device.
      try {
        addDoc(collection(db, 'feedback'), {
          outcome,
          rank: pending.rank ?? null,
          rating: pending.rating == null ? null : Math.round(pending.rating * 10) / 10,
          reviewBucket: reviewBucket(pending.reviewCount),
          walkMinutes: pending.walkMinutes == null ? null : Math.min(60, Math.max(0, Math.round(pending.walkMinutes))),
          createdAt: serverTimestamp()
        }).catch(() => {});
      } catch { /* Feedback is best-effort; local outcomes are already saved. */ }
    }
    return this.getOutcomes();
  }

  getOutcomes() {
    const history = read(HISTORY_KEY, []);
    return Object.fromEntries((Array.isArray(history) ? history : [])
      .filter(item => typeof item?.placeId === 'string' && ['liked', 'meh'].includes(item.outcome))
      .map(item => [item.placeId, item.outcome]));
  }

  clear() {
    write(PENDING_KEY, null);
    write(HISTORY_KEY, null);
  }
}

export default new VisitFeedbackService();
